import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * A menu button with arbitrary content in the popover — spec 092.
 *
 * `KebabMenu` already does portal + placement + keyboard + focus return, but its
 * contract is a flat `items` array of label/onSelect rows. The Export menu needs
 * mixed content (format actions, persistent checkboxes, a live row count), and the
 * filter pills in Pieces 2-3 need arbitrary controls behind a pill trigger. Rather
 * than fork that logic a third time, the machinery lives here and the caller
 * supplies the body.
 *
 * WHY A PORTAL (the same reason KebabMenu learned it): an `absolute` child is
 * clipped by ANY ancestor with `overflow: hidden`, and this menu hangs off Bar 1,
 * which sits above cards and grids that clip. A portal plus `position: fixed`
 * cannot be clipped by construction. `z-[60]` because Modal and ReportJobDrawer
 * are both `z-50` and winning a tie on portal-append order breaks silently when
 * someone reorders a tree.
 *
 * Accessibility, because the standing gate is that a passing aria-label proves
 * nothing about operability:
 *   - trigger: aria-haspopup="menu" + aria-expanded + aria-controls
 *   - ArrowDown/ArrowUp on the trigger opens
 *   - menu: role="menu"; caller marks rows role="menuitem" / "menuitemcheckbox"
 *   - Arrow/Home/End rove between those rows, Escape closes, focus returns
 *   - click-outside closes
 *
 * The caller's body gets `{ close }` so an action row can dismiss the menu while a
 * checkbox row can deliberately leave it open — which is the whole point of a menu
 * that holds settings as well as actions.
 */

const GAP = 6
const EST_H = 280
const EST_W = 272
const ITEM_SELECTOR = '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"]'
// What Tab can land on inside the popover — `tabOutToTrigger` (spec 181) only.
const TABBABLE = 'input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])'

const PopoverMenu = ({
  /*
   * 'menu'   — rows are actions/settings; arrow keys rove between them.
   * 'dialog' — the body holds real form controls (a date range, a typeahead, a
   *            number pair). Roving MUST be off there: it preventDefaults
   *            Arrow/Home/End, which is exactly how a user moves the caret
   *            inside a text input. A filter pill that ate your arrow keys
   *            while you edited a date would be unusable.
   */
  role = 'menu',
  /** Accessible name of the popover itself. */
  label,
  /** Accessible name of the trigger button. Defaults to `label`. */
  triggerLabel,
  triggerContent,
  triggerClassName = 'btn-rc',
  align = 'right',
  /* Pixels. `null` (not undefined, which takes the default) writes no inline
     width, so the popover sizes to its content; `place()` measures the rendered
     width either way. The list-page Columns picker relies on that (spec 181). */
  width = 272,
  disabled = false,
  children,
  'data-testid': testId,
  /*
   * spec 094 — a stable selector for Pendo Feature tagging, landed on the
   * TRIGGER button. Mirrors the existing data-testid passthrough because this
   * component destructures explicitly and does not spread rest props, so an
   * attribute passed by a caller would otherwise be dropped silently.
   *
   * Purely additive: undefined renders no attribute, so tab order, roles and
   * accessible names are untouched for every existing caller.
   */
  'data-pendo': pendoName,
  /*
   * spec 181 (CC-53473) — the trigger's hover tooltip, for the shared grid's
   * list-page Columns picker, which had one ("Show or hide columns") before it
   * moved onto this component. Additive in the same way as `data-pendo` above:
   * undefined renders no attribute, so every existing caller is unchanged.
   */
  title,
  /*
   * spec 181 (CC-53473) — OPT-IN, default off, so every existing caller keeps
   * exactly the Tab behaviour it has today.
   *
   * The popover is portalled to the end of <body>, so the page's Tab order does
   * not run through it: Tab past its last control leaves the document from its
   * END, and Shift+Tab before its first lands on whatever happens to precede the
   * portal. For a popover whose rows are Tab-navigated form controls (the
   * list-page Columns picker, which used to be an in-flow menu right after its
   * trigger), that is a focus-order regression. With this on, leaving either
   * edge closes the popover and resumes the page's own order at the trigger:
   * Shift+Tab lands ON the trigger; Tab moves focus to the trigger inside the
   * keydown and lets the browser's own Tab step proceed from there, i.e. to
   * whatever follows the trigger — where the in-flow menu used to send it.
   */
  tabOutToTrigger = false,
}) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const menuId = useId()

  /*
   * CLOSING IS DECIDED BY ARIA ROLE, not by a callback handed to the caller.
   *
   * The obvious API is a render-prop giving the body a `close()`. It is the wrong
   * one twice over. Mechanically, any callback that touches `ref.current` becomes
   * a render-time ref read when it is passed during render, which react-hooks/refs
   * rejects — correctly, because that is how a component reads a value React has
   * not committed. And in design terms it makes every caller remember to call it.
   *
   * The roles already carry the intent: `menuitem` is an ACTION, so choosing one
   * dismisses the menu; `menuitemcheckbox` is a SETTING, so ticking one leaves it
   * open — otherwise choosing two export options costs four clicks. Delegating on
   * the container reads that intent straight off the markup, and it runs in an
   * event handler where touching refs is fine.
   *
   * Focus restore is split out into an effect for the same reason: the flag says
   * "the user chose something or pressed Escape" (focus belongs back on the
   * trigger, or a keyboard user is stranded at the top of the document) as opposed
   * to "the user clicked somewhere else entirely" (leave their focus alone).
   */
  const restoreOnClose = useRef(false)

  const onMenuClick = (e) => {
    // Auto-close-on-action is menu semantics. A dialog stays open until the user
    // dismisses it — its body is a form, and closing on the first click inside
    // would make a two-field filter impossible to set.
    if (role !== 'menu') return
    // menuitem = an action, menuitemRADIO = a completed single choice: both are
    // "done, close me". menuitemCHECKBOX is deliberately excluded — Display View's
    // column toggles are multi-select and closing on the first tick would make the
    // panel unusable. Radio joined this list on 2026-08-26 when Group by / Filter
    // moved off native <select>s; nothing else in the tree used the role.
    if (!e.target.closest('[role="menuitem"],[role="menuitemradio"]')) return
    restoreOnClose.current = true
    setOpen(false)
  }

  useEffect(() => {
    if (open || !restoreOnClose.current) return
    restoreOnClose.current = false
    triggerRef.current?.focus()
  }, [open])

  /* Measured from the menu's own ref callback — i.e. after it is in the DOM, so
     the height is the REAL rendered height. An estimate flips the wrong way for a
     tall menu, and measuring in an effect body is the cascading-render hazard the
     repo's lint rules reject. */
  const place = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const r = trigger.getBoundingClientRect()
    const h = menuRef.current?.offsetHeight || EST_H
    const w = menuRef.current?.offsetWidth || width || EST_W
    const vh = window.innerHeight
    const vw = window.innerWidth
    const flipUp = vh - r.bottom < h + GAP && r.top > vh - r.bottom
    const top = Math.max(GAP, Math.min(flipUp ? r.top - h - GAP : r.bottom + GAP, vh - h - GAP))
    const left = Math.max(GAP, Math.min(align === 'right' ? r.right - w : r.left, vw - w - GAP))
    setCoords({ top, left, placement: flipUp ? 'up' : 'down' })
  }, [align, width])

  const attachMenu = useCallback((el) => {
    menuRef.current = el
    if (el) place()
  }, [place])

  // Reposition rather than close on scroll: a menu that vanishes when the user
  // nudges the wheel reads as a broken click. Capture phase so scrolls on inner
  // containers are seen too.
  useEffect(() => {
    if (!open) return undefined
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      restoreOnClose.current = true
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the first row on open, so a keyboard user lands inside the menu rather
  // than being left on the trigger with an open popover they cannot reach.
  useEffect(() => {
    if (!open) return
    const first = menuRef.current?.querySelector(ITEM_SELECTOR)
      || menuRef.current?.querySelector('input,select,textarea,button,[tabindex]:not([tabindex="-1"])')
    first?.focus()
  }, [open])

  const rove = (e) => {
    // See the `role` prop: a dialog's body holds real inputs, and these are the
    // keys that move a caret. Roving there would eat them.
    if (role !== 'menu') return
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(e.key)) return
    const list = [...(menuRef.current?.querySelectorAll(ITEM_SELECTOR) || [])]
      .filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true')
    if (!list.length) return
    e.preventDefault()
    const at = list.indexOf(document.activeElement)
    const next =
      e.key === 'Home' ? 0
        : e.key === 'End' ? list.length - 1
          : (at + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length
    list[next]?.focus()
  }

  // `tabOutToTrigger` (spec 181) — see the prop. A no-op for every caller that
  // does not opt in, and for any key but Tab.
  const tabOut = (e) => {
    if (!tabOutToTrigger || e.key !== 'Tab') return
    const list = [...(menuRef.current?.querySelectorAll(TABBABLE) || [])]
    if (!list.length) return
    const edge = e.shiftKey ? list[0] : list[list.length - 1]
    if (document.activeElement !== edge) return
    // Backwards: the trigger IS the destination, so the browser's step is
    // cancelled. Forwards: the browser's step is kept, and starts from the
    // trigger because focus is moved there before it runs.
    if (e.shiftKey) e.preventDefault()
    setOpen(false)
    triggerRef.current?.focus()
  }

  const onPopoverKeyDown = (e) => {
    tabOut(e)
    rove(e)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup={role}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel || label}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true) }
        }}
        className={`${triggerClassName}${open ? ' is-active' : ''}`}
        title={title}
        data-testid={testId}
        data-pendo={pendoName}
      >
        {triggerContent}
      </button>

      {open && createPortal(
        <div
          ref={attachMenu}
          id={menuId}
          role={role}
          aria-label={label}
          data-placement={coords?.placement || 'down'}
          onKeyDown={onPopoverKeyDown}
          onClick={onMenuClick}
          style={{ position: 'fixed', top: coords?.top ?? 0, left: coords?.left ?? 0, width }}
          /* No overflow-hidden: a filter pill's popover will hold controls that
             open their OWN popovers (the dropdown and lookup filters already work
             that way), and clipping them is the DataGrid kebab bug again.

             A2 (Himaad, 2026-08-26): py-2, was py-1.5 — 8px of breathing room
             above the first row and below the last, not 6px. This is the ONLY
             shared vertical-padding lever for "more padding across the whole
             dropdown", and four surfaces ride it: Display View (flat), Display
             View (grouped), the Export menu, and every Bar 2 filter pill on all
             140 reports. There is deliberately NO horizontal padding here —
             every row supplies its own, so a row can span the full width for
             its hover band. (Those rows are not yet consistent with each other:
             the two menus use px-3.5 and FilterPill's body uses px-3 py-2.5.
             Divergent inside the shared primitive, noted, out of scope here.)

             border-rc-line is correct on this element and must NOT move to
             border-rc-control: this is a card outline around a floating panel,
             not the only affordance identifying a control. */
          className="z-[60] rounded-rc-card border border-rc-line bg-white py-2 shadow-lg"
        >
          {children}
        </div>,
        document.body
      )}
    </>
  )
}

export default PopoverMenu
